/**
 * BlockYjsService - Yjs CRDT layer for block content conflict resolution
 *
 * Phase 3 of the sync improvements: Yjs for block content
 * Architecture: No Hocuspocus server needed (single user).
 * Supabase is the transport — crdt_state column stores the encoded Yjs state.
 *
 * Flow:
 *   Device A edits → Y.Text updated → state encoded as base64 → saved to DB
 *   Device B receives Realtime event → applies encoded state to its Y.Doc → no conflicts
 */

import * as Y from 'yjs';

// Cache of Y.Doc instances keyed by block ID
// Kept in memory for the session lifetime; no separate IndexedDB persistence needed
// (the crdtState is stored in the blocks table alongside content)
const ydocCache = new Map<string, Y.Doc>();

/**
 * Get or create a Y.Doc for a block.
 * The Y.Doc contains a single Y.Text at key 'content'.
 */
export function getBlockYDoc(blockId: string): Y.Doc {
  if (ydocCache.has(blockId)) {
    return ydocCache.get(blockId)!;
  }
  const ydoc = new Y.Doc();
  ydocCache.set(blockId, ydoc);
  return ydoc;
}

/**
 * Initialize a Y.Doc's content from a stored crdtState.
 * Call this when loading a block that already has a crdtState.
 * Safe to call multiple times — Yjs update application is idempotent.
 */
export function initYDocFromState(blockId: string, crdtStateBase64: string): void {
  const ydoc = getBlockYDoc(blockId);
  try {
    const updateBytes = base64ToUint8Array(crdtStateBase64);
    Y.applyUpdate(ydoc, updateBytes);
  } catch (err) {
    console.warn(`[BlockYjsService] Failed to apply stored state for block ${blockId}:`, err);
  }
}

/**
 * Set the text content of a block's Y.Doc.
 * This creates a Yjs transaction that can be merged with remote changes.
 * Returns the encoded state as a base64 string for storage.
 */
export function setBlockContent(blockId: string, content: string): string {
  const ydoc = getBlockYDoc(blockId);
  const ytext = ydoc.getText('content');

  // Use a Yjs transaction to batch the update
  ydoc.transact(() => {
    // Replace the full text content
    // This is safe for single-user use: we own this device's state
    if (ytext.toString() !== content) {
      ytext.delete(0, ytext.length);
      ytext.insert(0, content);
    }
  });

  return encodeYDocState(blockId);
}

/**
 * Apply a remote Yjs state update to a local Y.Doc.
 * This is the CRDT merge — applying updates in any order gives the same result.
 * Returns the merged content as a string.
 */
export function applyRemoteUpdate(blockId: string, remoteCrdtStateBase64: string): string {
  const ydoc = getBlockYDoc(blockId);
  try {
    const updateBytes = base64ToUint8Array(remoteCrdtStateBase64);
    Y.applyUpdate(ydoc, updateBytes);
    const ytext = ydoc.getText('content');
    return ytext.toString();
  } catch (err) {
    console.warn(`[BlockYjsService] Failed to apply remote update for block ${blockId}:`, err);
    return ''; // Caller should fall back to timestamp-based content
  }
}

/**
 * Encode the current Y.Doc state as a base64 string for storage.
 * Y.encodeStateAsUpdate() captures the full document state,
 * not just a delta — any device can start from this to get the correct content.
 */
export function encodeYDocState(blockId: string): string {
  const ydoc = getBlockYDoc(blockId);
  const stateBytes = Y.encodeStateAsUpdate(ydoc);
  return uint8ArrayToBase64(stateBytes);
}

/**
 * Get the current text content from a block's Y.Doc.
 */
export function getBlockContent(blockId: string): string {
  const ydoc = getBlockYDoc(blockId);
  return ydoc.getText('content').toString();
}

/**
 * Remove a Y.Doc from the cache (cleanup when block is deleted or object closed).
 */
export function destroyBlockYDoc(blockId: string): void {
  const ydoc = ydocCache.get(blockId);
  if (ydoc) {
    ydoc.destroy();
    ydocCache.delete(blockId);
  }
}

/**
 * Clear all cached Y.Docs (call on logout to prevent cross-user state leakage).
 */
export function clearAllYDocs(): void {
  for (const [, ydoc] of ydocCache) {
    ydoc.destroy();
  }
  ydocCache.clear();
}

// ────────────────────────────────────────────────────────────────────────────
// Utility: binary ↔ base64 conversions
// ────────────────────────────────────────────────────────────────────────────

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
